/**
 * Spec 2026-08-25 — voorraadkoppeling per maat-variant: pure helpers die het
 * koppel-endpoint, de aanmaak-service én de backfill delen, zodat een maat en
 * een categorie overal dezelfde voorraad-waarde krijgen.
 */
import { canoniekeMaten, isMaatloos, maatIsEenduidig, toonMaat } from './uitrusting-maat'

/**
 * De maat zoals hij op het gekoppelde VoorraadArtikel komt te staan: de
 * catalogus-schrijfwijze (`L`, `3XL`, `52 PLUS`), `null` voor maatloos. Niet
 * eenduidig canoniseerbaar (bereik "44/45") -> de ruwe variant-maat, zodat er
 * geen informatie verdwijnt.
 */
export function voorraadMaatVoorVariant(
  maat: string | null | undefined,
  maatType: string | null | undefined,
): string | null {
  if (maat == null || maat.trim().length === 0 || isMaatloos(maat)) return null
  if (!maatIsEenduidig(maat, maatType)) return maat.trim()
  const [canoniek] = canoniekeMaten(maat, maatType)
  return canoniek ? toonMaat(canoniek) : maat.trim()
}

// batch-69 samenstellen (FO-510 §C): MERCHANDISE (STREEP-producten) landt in de
// voorraad als OVERIG; het eigendom volgt uit de catalogus-categorie.
export type UitrustingCategorieCode = 'KLEDING' | 'GEREEDSCHAP' | 'PBM' | 'VEILIGHEID' | 'MERCHANDISE'
export type VoorraadCategorieVoorVariant = 'WERKKLEDING' | 'GEREEDSCHAP' | 'PBM' | 'OVERIG'

/** Voorraad-categorie voor een uit de catalogus aangemaakt voorraadartikel. */
export function voorraadCategorieVoorUitrusting(
  categorie: UitrustingCategorieCode,
): VoorraadCategorieVoorVariant {
  switch (categorie) {
    case 'KLEDING':
      return 'WERKKLEDING'
    case 'GEREEDSCHAP':
      return 'GEREEDSCHAP'
    case 'PBM':
    case 'VEILIGHEID':
      return 'PBM'
    case 'MERCHANDISE':
      return 'OVERIG'
  }
}

/**
 * Tokens van een artikelnaam voor de backfill-match: diacritics weg, kleine
 * letters, ®/™ weg, gesplitst op alles wat geen letter of cijfer is,
 * gesorteerd. "T-shirt - Santino Lebec" en "Santino T-shirt Lebec" geven
 * dezelfde tokens; "Blåkläder" en "Blaklader" ook (de voorraad schrijft
 * merknamen niet altijd met accenten).
 */
export function normaliseerArtikelnaamVoorMatch(naam: string): string[] {
  return naam
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[®™]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0)
    .sort()
}

function gedeeldeTokens(a: string, b: string): { gedeeld: number; ta: Set<string>; tb: Set<string> } {
  const ta = new Set(normaliseerArtikelnaamVoorMatch(a))
  const tb = new Set(normaliseerArtikelnaamVoorMatch(b))
  let gedeeld = 0
  for (const t of ta) if (tb.has(t)) gedeeld += 1
  return { gedeeld, ta, tb }
}

/** Overlap-score 0..1 = |A ∩ B| / min(|A|, |B|); 0 als een van beide leeg is. */
export function artikelnaamOverlap(a: string, b: string): number {
  const { gedeeld, ta, tb } = gedeeldeTokens(a, b)
  if (ta.size === 0 || tb.size === 0) return 0
  return gedeeld / Math.min(ta.size, tb.size)
}

/**
 * Jaccard-score 0..1 = |A ∩ B| / |A ∪ B|; 0 als een van beide leeg is. Straft
 * - anders dan `artikelnaamOverlap` - de tokens die NIET gedeeld worden af, en
 * kiest daarmee tussen kandidaten die allebei boven de overlap-drempel liggen
 * ("Santino T-shirt Lebec" wint van "Santino T-shirt Ledburg").
 */
export function artikelnaamJaccard(a: string, b: string): number {
  const { gedeeld, ta, tb } = gedeeldeTokens(a, b)
  if (ta.size === 0 || tb.size === 0) return 0
  return gedeeld / (ta.size + tb.size - gedeeld)
}
